import React, { useMemo } from 'react';
import moment from 'moment';
import { Button } from 'components';
import { useParams } from 'react-router-dom';
import { CLIENT_EMAIL_ATTACHMENT_TYPE } from 'constants/index';
import { DownloadIcon } from 'components/common/icons/DownloadIcon';
import { Typography, Collapse, Row, Col, Space, Spin, Empty } from 'components';
import { useAttachmentListQuery, useGetEmailQuery, usePurchaseRequestByIdQuery } from 'api';
import styles from './styles.module.scss';
import { Skeleton } from 'antd';

const { Panel } = Collapse;
const { Text, Title } = Typography;

export const EmailContent = React.memo(() => {
  const { id } = useParams() as { id: string };
  const { data: purchaseRequestDetails = {}, isLoading: isLoadingDetails } = usePurchaseRequestByIdQuery({ id });
  const { data: attachments = {}, isLoading: isLoadingAttachments } = useAttachmentListQuery(
    {
      entityName: 'LEAD',
      entityId: purchaseRequestDetails.purchaseRequest?.leadId,
      page: 1,
      size: 20,
      filters: {
        attachmentTypes: [CLIENT_EMAIL_ATTACHMENT_TYPE],
      },
    },
    { skip: !purchaseRequestDetails.purchaseRequest?.leadId }
  );
  const { data: { data: emailData } = {}, isLoading: isLoadingEmail } = useGetEmailQuery(
    { id: purchaseRequestDetails?.purchaseRequest?.emailId },
    { skip: !purchaseRequestDetails?.purchaseRequest?.emailId }
  );

  const formatDate = (date: string) => {
    return moment(date).format('DD.MM.YYYY HH:mm:ss');
  };

  const emailContent = useMemo(() => {
    if (isLoadingEmail || isLoadingDetails) {
      return <Skeleton active />;
    }
    if (!purchaseRequestDetails?.purchaseRequest?.emailId || !emailData) {
      return <Empty style={{ marginTop: '10%' }} />;
    }
    return (
      <Row className={styles.email_container}>
        <Col className={styles.email_left}>
          <Space direction="vertical" className={styles.email_header}>
            <Text className={styles.subject} ellipsis={{ tooltip: true }}>
              <strong>Subject:</strong> {emailData.subject}
            </Text>
            <Text className={styles.from} ellipsis={{ tooltip: true }}>
              <strong>From:</strong> {emailData.fromEmail}
            </Text>
            <Text className={styles.time} ellipsis={{ tooltip: true }}>
              <strong>Time of arrival:</strong> {formatDate(emailData.receivedDate)}
            </Text>
          </Space>

          {attachments.content && attachments.content.length > 0 && (
            <Spin spinning={isLoadingAttachments} className={styles.attachments}>
              <Title level={5} className={styles.attachment_title}>
                Attachments
              </Title>
              <Space direction="vertical" className={styles.attachment_list}>
                {attachments.content.map((attachment: any, index: number) => (
                  <Space key={index} className={styles.attachment_item}>
                    {attachment.files?.ATTACHMENTS ? (
                      <Button
                        type="link"
                        style={{ paddingLeft: 0 }}
                        icon={<DownloadIcon />}
                        download
                        target="_blank"
                        className={styles.btn_download}
                        title={attachment.fileName}
                        rel="noopener noreferrer"
                        href={attachment.files?.ATTACHMENTS}
                      >
                        <Text ellipsis={{ tooltip: true }} className={styles.btn_download_text}>
                          {attachment.fileName || 'Not Set'}
                        </Text>
                      </Button>
                    ) : (
                      ''
                    )}
                  </Space>
                ))}
              </Space>
            </Spin>
          )}
        </Col>

        <Col className={styles.email_content}>
          <Spin spinning={isLoadingDetails || isLoadingEmail}>
            <iframe
              srcDoc={emailData.mailBodyHTML?.replace(/<a\s+(?![^>]*target=)/g, '<a target="_blank" ')}
              className={'html-content-iframe'}
              style={{ width: '95%', height: 400 }}
              sandbox="allow-popups"
            />
          </Spin>
        </Col>
      </Row>
    );
  }, [
    purchaseRequestDetails?.purchaseRequest,
    emailData,
    attachments,
    isLoadingAttachments,
    isLoadingDetails,
    isLoadingEmail,
  ]);

  return (
    <Collapse defaultActiveKey={['1']} className={styles.email_collapse} expandIconPosition="end">
      <Panel
        header={
          <Title level={4} className={styles.client_email_title}>
            Client Original Email
          </Title>
        }
        key="1"
      >
        {emailContent}
      </Panel>
    </Collapse>
  );
});
